/**
 * The switchboard client: one WebSocket to the Worker, used only to get the peers
 * introduced to each other.
 *
 * Everything here is short-lived by design. Once the data channels are open this
 * socket carries nothing but the occasional join and leave — and if it dropped
 * entirely, an established match would keep playing, because no gameplay byte has
 * ever gone through it.
 */

export const TUNING = {
  /**
   * Where the signalling Worker lives. Overridable from the URL so a LAN test can
   * point at `wrangler dev` without a rebuild, and so a self-hosted copy can point
   * at its own.
   */
  defaultUrl: 'https://blackout-signal.satoringo2.workers.dev',
  pingMs: 20_000,
  /** Reconnect backoff while a room is meant to be open. */
  retryMs: [400, 900, 2000, 4000],
};

export function signalBase() {
  const q = new URLSearchParams(location.search).get('signal');
  if (q) return q.replace(/\/$/, '');
  try {
    const saved = localStorage.getItem('ob.signal');
    if (saved) return saved.replace(/\/$/, '');
  } catch {
    /* private browsing */
  }
  return TUNING.defaultUrl;
}

function wsUrl(base, path) {
  const u = new URL(path, base + '/');
  u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
  return u.toString();
}

export class Signal extends EventTarget {
  constructor(base = signalBase()) {
    super();
    this.base = base;
    this.ws = null;
    this.id = null;
    this.isHost = false;
    this.code = null;
    this.closed = false;
    this._pingTimer = 0;
  }

  /** Ask the Worker for a fresh room code. Does not open the socket. */
  async createCode() {
    const res = await fetch(`${this.base}/new`, { method: 'GET' });
    if (!res.ok) throw new Error(`signal: ${res.status}`);
    const { code, error } = await res.json();
    if (!code) throw new Error(error ?? 'no code');
    return code;
  }

  /**
   * Join a room and stay connected. Resolves once the Worker has said who we are;
   * peers arrive as events afterwards.
   */
  connect(code, name = 'PLAYER') {
    this.code = code;
    this.closed = false;
    return new Promise((resolve, reject) => {
      let settled = false;
      const url = wsUrl(this.base, `join?code=${encodeURIComponent(code)}&name=${encodeURIComponent(name)}`);
      const ws = new WebSocket(url);
      this.ws = ws;

      ws.addEventListener('open', () => {
        this._pingTimer = setInterval(() => this.send({ t: 'ping' }), TUNING.pingMs);
      });

      ws.addEventListener('message', (e) => {
        let msg;
        try {
          msg = JSON.parse(e.data);
        } catch {
          return;
        }
        if (msg.t === 'welcome') {
          this.id = msg.id;
          this.isHost = !!msg.host;
          if (!settled) {
            settled = true;
            resolve(msg);
          }
        }
        this.dispatchEvent(new CustomEvent(msg.t, { detail: msg }));
        this.dispatchEvent(new CustomEvent('*', { detail: msg }));
      });

      ws.addEventListener('close', (e) => {
        clearInterval(this._pingTimer);
        // A 409 from the room-full check arrives as a failed upgrade, so the only
        // signal a caller gets is the socket closing before `welcome`.
        if (!settled) {
          settled = true;
          reject(new Error(e.reason || 'could not join'));
        }
        this.dispatchEvent(new CustomEvent('closed', { detail: { reason: e.reason } }));
      });

      ws.addEventListener('error', () => {
        if (!settled) {
          settled = true;
          reject(new Error('signalling server unreachable'));
        }
      });
    });
  }

  send(obj) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(obj));
  }

  close() {
    this.closed = true;
    clearInterval(this._pingTimer);
    try {
      this.ws?.close(1000, 'bye');
    } catch {
      /* already gone */
    }
    this.ws = null;
  }
}
